import type { AppNavOverlayMode } from '../../types';
import { AppNavMaxOpacity, AppNavShiftLogoBy } from '../constants';

interface Options {
  overlay?: boolean;
  mode?: AppNavOverlayMode;
  isMenuOpen?: boolean;
  shiftLogoUp?: boolean;
}

interface ScrollStyle {
  /** The overlay mode, that can be overridden by scroll. */
  mode: AppNavOverlayMode;
  /** The background color of the root element. */
  backgroundColor: string;
  /** The box-shadow of the root element. */
  boxShadow: string;
  /** Translate the logo vertically while scrolling. */
  logoTranslateY: number;
}

/** Returns the styles to use while scrolling. */
export function getScrollStyle(
  scrollProgress: number,
  options: Options
): ScrollStyle {
  const {
    overlay = false,
    mode = 'light',
    isMenuOpen = false,
    shiftLogoUp = false,
  } = options;

  const bgOpacity = getBgOpacity(isMenuOpen, overlay, scrollProgress);
  const shadowOpacity = getShadowOpacity(scrollProgress, {
    isMenuOpen,
  });
  const logoTranslate = getLogoTranslateY(scrollProgress, {
    isMenuOpen,
    shiftLogoUp,
  });
  const scrollMode = getModeAfterScroll(scrollProgress, {
    isMenuOpen,
    overlay,
    mode,
  });

  return {
    mode: scrollMode,
    backgroundColor: `rgba(255, 255, 255, ${bgOpacity})`,
    boxShadow: `0 2px 12px 0 rgba(0, 0, 0, ${shadowOpacity})`,
    logoTranslateY: logoTranslate,
  };
}

function getBgOpacity(
  isMenuOpen: boolean,
  overlay: boolean,
  scrollProgress: number
) {
  return isMenuOpen || !overlay ? 1 : scrollProgress;
}
function getLogoTranslateY(
  scrollProgress: number,
  options?: {
    isMenuOpen: boolean;
    shiftLogoUp: boolean;
  }
) {
  if (options?.isMenuOpen) {
    return AppNavShiftLogoBy;
  }
  const logoDown = !options?.shiftLogoUp || scrollProgress >= 0.7;
  return logoDown ? AppNavShiftLogoBy : AppNavShiftLogoBy * -1;
}

/**
 * Given a percent, max, and min values, determine the number that is some
 * percent between max and min.
 *
 * @example GetProgressOfRange(0.5, 0, 10); // 5
 */
function getProgressOfRange(percent: number, min: number, max: number) {
  return (percent * 100 * (max - min)) / 100 + min;
}

function getShadowOpacity(
  scrollProgress: number,
  options?: {
    isMenuOpen: boolean;
  }
) {
  if (options?.isMenuOpen) {
    return AppNavMaxOpacity;
  }
  const opacity = getProgressOfRange(scrollProgress, 0, AppNavMaxOpacity);
  return opacity;
}

function getModeAfterScroll(
  scrollProgress: number,
  options?: {
    isMenuOpen: boolean;
    overlay: boolean;
    mode: AppNavOverlayMode;
  }
): AppNavOverlayMode {
  if (options?.isMenuOpen || (options?.overlay && options.mode === 'dark')) {
    return 'light';
  }
  return scrollProgress > 0.7 ? 'light' : 'dark';
}
